import { css } from "@emotion/css";
import { CoffeeSpiltIllustration, QuestionIllustration } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { GitRef, ProjectResource, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { BranchSelector } from "~/areas/projects/components/BranchSelector/BranchSelector";
import InternalLink from "~/components/Navigation/InternalLink";
interface VcsErrorProps {
    project: ProjectResource;
    error: ValidateGitRefV2Response;
    gitRef: GitRef | undefined;
    setGitRef: (gitRef: GitRef) => void;
}
export function VcsError({ project, error, gitRef, setGitRef }: VcsErrorProps) {
    const errorIllustration = getErrorIllustration(error);
    const errorTitle = getErrorTitle(error);
    const errorMessage = getErrorMessage(error);
    const errorInfoText = getErrorInfoText(error, project.Slug, project.SpaceId);
    const showBranchSelector = error.Type !== ValidateGitRefV2ResponseType.ConnectionFailed;
    const branchSelectorErrorMessage = getBranchSelectorErrorMessage(error);
    return (<div className={containerStyles}>
            {errorIllustration}
            <h2 className={headerStyles}>{errorTitle}</h2>
            <p className={paragraphStyles}>
                {errorMessage}
                <br />
                {errorInfoText}
            </p>
            {showBranchSelector && (<div>
                    <BranchSelector project={project} gitRef={gitRef} setGitRef={setGitRef} errorMessage={branchSelectorErrorMessage}/>
                </div>)}
        </div>);
}
function getErrorIllustration(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return <QuestionIllustration />;
        default:
            return <CoffeeSpiltIllustration />;
    }
}
function getErrorTitle(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.GitRefNotFound:
            return "Could not load the selected git ref";
        default:
            return "Could not access version control";
    }
}
function getErrorMessage(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return "We're unable to establish a connection to the git instance.";
        default:
            return error.Message;
    }
}
function getBranchSelectorErrorMessage(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.GitRefNotFound:
            return "unknown git ref";
        case ValidateGitRefV2ResponseType.OclNotFound:
            return "ocl not found";
        default:
            return undefined;
    }
}
function getErrorInfoText(error: ValidateGitRefV2Response, projectSlug: string, spaceId: string) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return (<>
                    This could be due to an outage or check your <InternalLink to={links.projectVersionControlSettingsPage.generateUrl({ spaceId, projectSlug })}>Version Control Settings</InternalLink>.
                </>);
        default:
            return (<>
                    <span className={infoTextStyles}>Try selecting a different branch below</span>.
                </>);
    }
}
const containerStyles = css({
    display: "flex",
    flexDirection: "column",
    padding: `${space[32]} ${space[32]} ${space[80]}`,
    gap: space[16],
    alignItems: "center",
});
const headerStyles = css({
    margin: 0,
    font: text.heading.large,
});
const paragraphStyles = css({
    margin: 0,
    font: text.body.regular.large,
    textAlign: "center",
});
const infoTextStyles = css({
    color: themeTokens.color.text.info,
});
